// "Finish setup: create your first pass" (ADR-0058): a standing note at the head of the Floor of a venue still setting
// up that has no pass on sale — the essential How players pay (`price`) missing from its setup. It stays until a pass is
// on sale, and **Create a pass** opens How players pay right here, in its centred dialog.
//
// It blocks nothing: staff can still add time and run the floor. Go live already waits on a pass (the server's gate),
// and a venue with pay as you go off and no pass cannot start a kiosk session — this says why, where staff look first.
// It reads the venue's setup (`settings:read`), whose missing essentials are listed only while the venue is in `setup`,
// so a venue that has opened never sees it. A member who may not make passes is told, without the button.

import { useTranslation } from '@clavian/i18n/react';
import { Button } from '@clavian/ui-web';
import { useState, type ReactNode } from 'react';

import { useAccess } from '../api/queries';
import { useCurrentVenue } from '../shell/scope';
import { Notice } from '../ui';
import { HowPlayersPayDialog } from './HowPlayersPayDialog';
import { useVenueSetup } from './queries';

import './setup.css';

const SETTINGS_READ = 'settings:read';
const PASSES_MANAGE = 'passes:manage';

export function FinishSetupBanner(): ReactNode {
  const { t } = useTranslation('console');
  const venue = useCurrentVenue();
  const access = useAccess({ venueId: venue.id });
  const ready = access.status === 'ready';
  const setup = useVenueSetup(venue.organizationId, venue.id, ready && access.can(SETTINGS_READ));
  const [open, setOpen] = useState(false);
  const missing = setup.data?.missingEssentials.includes('price') === true;
  const canMake = ready && access.can(PASSES_MANAGE);

  if (!missing && !open) return null;
  return (
    <>
      {missing ? (
        <Notice
          tone="warn"
          className="console-finish-setup"
          title={t('setup.floorBanner.title')}
          action={
            canMake ? (
              <Button variant="primary" size="sm" onClick={() => setOpen(true)}>
                {t('setup.floorBanner.action')}
              </Button>
            ) : undefined
          }
        >
          {t('setup.floorBanner.body')}
        </Notice>
      ) : null}
      {canMake ? <HowPlayersPayDialog open={open} onOpenChange={setOpen} /> : null}
    </>
  );
}
